import { useCallback, useRef, useState } from 'react';

import { DocumentStatusFilter } from 'src/components/reports/report-detail/GridStatusCell';
import type { DocumentStatusFilterType } from 'src/components/reports/report-detail/GridStatusCell';
import {
  useDocxUploadDocumentLookup,
  useUploadAsLatestVersion,
} from 'src/helpers/docx-upload-target';
import {
  useCreateFilestackClient,
  useGetFreshFilestackClient,
} from 'src/helpers/integrations/filestack';
import type { FilestackUploadControlToken } from 'src/helpers/integrations/filestack-multiupload';
import { useRestoreTrashedDocuments, useTrashDocuments } from 'src/hooks/document';
import { useFileDragState } from 'src/hooks/drag-and-drop';
import { useCreateFiles } from 'src/hooks/file';
import { useCreateStaticFileVersions } from 'src/hooks/file-version';
import { useNotificationDispatch } from 'src/notifications';
import { Sentry } from 'src/sentry';
import { v4 as uuid4 } from 'uuid';

import { persistRepositoryDropzoneUploads } from './helpers/persist/persistRepositoryDropzoneUploads';
import type { RepositoryDropzoneSuccessfulStackEntry } from './helpers/persist/persistRepositoryDropzoneUploads';
import { handleFilesDrop } from './helpers/processing/handleFilesDrop';
import { createUploadItems } from './helpers/upload/createUploadItems';
import { runRepositoryDropzoneMultiuploadBatch } from './helpers/upload/runRepositoryDropzoneMultiuploadBatch';
import type { RepositoryDropzoneUploadStackRow } from './helpers/upload/runRepositoryDropzoneMultiuploadBatch';
import { findUploadFile } from './helpers/utils/findUploadFile';
import { notifyRepositoryDropzoneError } from './helpers/utils/notifyRepositoryDropzoneError';
import { withUploadUnloadGuard } from './helpers/utils/uploadUnloadGuard';
import { statusToDocumentVariant } from '../repository/RepositoryNewButton';

export interface UploadFile {
  filename: string;
  id: string;
  progress: number;
  state: 'pending' | 'uploading' | 'persisting' | 'completed' | 'failed' | 'cancelled';
  documentId?: UUID;
  error?: string;
  fileSize?: number;
  isTrashed?: boolean;
  mimeType?: string;
  retryCount?: number;
  speed?: number;
  timeRemaining?: number;
}

interface PendingUpload {
  files: File[];
  totalSize: number;
}

interface UseRepositoryDropzoneParams {
  documentStatusFilter: DocumentStatusFilterType;
  repositoryId: UUID;
  disabled?: boolean;
  onDocumentsRestored?: (documentIds: readonly UUID[]) => void;
  onDocumentsTrashed?: (documentIds: readonly UUID[]) => void;
}

export function useRepositoryDropzone(params: UseRepositoryDropzoneParams) {
  const {
    disabled = false,
    documentStatusFilter,
    onDocumentsRestored,
    onDocumentsTrashed,
    repositoryId,
  } = params;

  const dispatch = useNotificationDispatch();
  const { createFiles } = useCreateFiles();
  const { createStaticFileVersions } = useCreateStaticFileVersions();
  const { confirmUploadAsLatestVersion, dialog } = useUploadAsLatestVersion();
  const { trashDocuments } = useTrashDocuments();
  const { restoreDocuments } = useRestoreTrashedDocuments();
  const getFreshFilestackClient = useGetFreshFilestackClient();
  const { filestackClient, loading: filestackLoading } = useCreateFilestackClient();
  const lookupDocument = useDocxUploadDocumentLookup();

  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isUploadPaused, setIsUploadPaused] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<UploadFile[]>([]);
  const [pendingUpload, setPendingUpload] = useState<PendingUpload | null>(null);

  /**
   * In-flight upload bookkeeping lives in refs and is never torn down by an
   * effect cleanup. Tab panes render inside React.Activity, which destroys
   * effects on every tab hide (not just unmount) while preserving state, so an
   * unmount-style cleanup that cancelled uploads or cleared these refs would
   * run on every tab switch — orphaning queued File blobs and failing uploads
   * with "File not found". Uploads intentionally keep running while the pane
   * is hidden.
   */
  const abortedIdsRef = useRef<Set<string>>(new Set());
  const fileRefsRef = useRef<Map<string, File>>(new Map());
  const uploadBatchesRef = useRef<
    Map<string, { fileIds: Set<string>; token: FilestackUploadControlToken }>
  >(new Map());

  /**
   * Mirror of isUploadPaused readable from the async batch flow. A pause clicked while a batch
   * is still minting fresh upload security has no SDK token to call yet, so the batch reads
   * this ref when it reaches the SDK and starts paused.
   */
  const isUploadPausedRef = useRef(false);

  const patchUploadFile = useCallback((fileId: string, patch: Partial<UploadFile>) => {
    setUploadFiles(prev => prev.map(f => (f.id === fileId ? { ...f, ...patch } : f)));
  }, []);

  // Uploads the given items to Filestack as one multiupload batch, then persists the successes.
  const runUploadBatch = useCallback(
    (items: UploadFile[]) =>
      withUploadUnloadGuard(async () => {
        /**
         * Register the batch before any awaits so pause/cancel clicks during the fresh-client
         * fetch below target this batch rather than finding an empty map.
         */
        const batchId = uuid4();
        const token: FilestackUploadControlToken = {};
        uploadBatchesRef.current.set(batchId, {
          fileIds: new Set(items.map(item => item.id)),
          token,
        });

        let stackResults: RepositoryDropzoneUploadStackRow[];
        try {
          /**
           * Upload security may have expired while the polled client sat idle (e.g. a slept tab).
           * Refresh it at batch start so long batches get the full policy window; fall back to the
           * polled client if the refresh fails so uploads still get attempted.
           */
          const freshFilestackClient = await getFreshFilestackClient().catch(() => filestackClient);

          stackResults = await runRepositoryDropzoneMultiuploadBatch({
            abortedIdsRef,
            fileRefsRef,
            filestackClient: freshFilestackClient,
            getFreshFilestackClient,
            items,
            setUploadFiles,
            startPaused: isUploadPausedRef.current,
            token,
          });
        } finally {
          uploadBatchesRef.current.delete(batchId);
          if (!uploadBatchesRef.current.size) {
            isUploadPausedRef.current = false;
            setIsUploadPaused(false);
          }
        }

        const successfulUploads = stackResults.filter(getIsSuccessfulUploadRow);
        if (!successfulUploads.length) return;

        await persistRepositoryDropzoneUploads({
          confirmUploadAsLatestVersion,
          createFiles,
          createStaticFileVersions,
          documentVariant: statusToDocumentVariant(documentStatusFilter),
          fileRefs: fileRefsRef.current,
          isSigned: documentStatusFilter === DocumentStatusFilter.Signed,
          lookupDocument,
          repositoryId,
          setUploadFiles,
          successfulOrdered: successfulUploads,
        });

        successfulUploads.forEach(({ id }) => fileRefsRef.current.delete(id));
      }),
    [
      confirmUploadAsLatestVersion,
      createFiles,
      createStaticFileVersions,
      documentStatusFilter,
      filestackClient,
      getFreshFilestackClient,
      lookupDocument,
      repositoryId,
    ]
  );

  const startUpload = useCallback(
    async (validFiles: File[]) => {
      if (filestackLoading || !validFiles.length) return;

      const newItems = createUploadItems(validFiles, fileRefsRef.current);
      setUploadFiles(prev => [...prev, ...newItems]);
      setIsPanelOpen(true);

      await runUploadBatch(newItems);
    },
    [filestackLoading, runUploadBatch]
  );

  const retryUpload = useCallback(
    async (fileId: string) => {
      if (filestackLoading) return;

      const fileToRetry = findUploadFile(uploadFiles, fileId);
      if (!fileToRetry || !getIsRetryableUpload(fileToRetry)) return;

      if (!fileRefsRef.current.has(fileId)) {
        Sentry.captureException(new Error(`File ref not found for retry, id: ${fileId}`), {
          tags: { feature: 'repository-dropzone' },
        });
        return;
      }

      patchUploadFile(fileId, { error: undefined, progress: 0, state: 'pending' });
      abortedIdsRef.current.delete(fileId);

      await runUploadBatch([fileToRetry]);
    },
    [filestackLoading, patchUploadFile, runUploadBatch, uploadFiles]
  );

  const pauseActiveUploads = useCallback(() => {
    isUploadPausedRef.current = true;
    uploadBatchesRef.current.forEach(({ token }) => token.pause?.());
    setIsUploadPaused(true);
  }, []);

  const resumeActiveUploads = useCallback(() => {
    isUploadPausedRef.current = false;
    uploadBatchesRef.current.forEach(({ token }) => token.resume?.());
    setIsUploadPaused(false);
  }, []);

  const cancelActiveUploads = useCallback(() => {
    uploadBatchesRef.current.forEach(({ fileIds, token }) => {
      fileIds.forEach(id => abortedIdsRef.current.add(id));
      token.cancel?.();
    });
    isUploadPausedRef.current = false;
    setIsUploadPaused(false);

    setUploadFiles(prev =>
      prev.map(f => {
        if (!getIsCancellableUpload(f)) return f;
        abortedIdsRef.current.add(f.id);
        return { ...f, state: 'cancelled' };
      })
    );
  }, []);

  const clearInactiveUploads = useCallback(() => {
    setUploadFiles(prev => {
      prev.filter(f => !getIsActiveUpload(f)).forEach(f => fileRefsRef.current.delete(f.id));
      return prev.filter(getIsActiveUpload);
    });
  }, []);

  const removeUpload = useCallback((fileId: string) => {
    setUploadFiles(prev => prev.filter(f => f.id !== fileId));
    fileRefsRef.current.delete(fileId);
  }, []);

  const deleteUpload = useCallback(
    async (fileId: string) => {
      const file = findUploadFile(uploadFiles, fileId);
      if (!file?.documentId) {
        removeUpload(fileId);
        return;
      }

      try {
        await trashDocuments({ documentIds: [file.documentId] });
        onDocumentsTrashed?.([file.documentId]);
        patchUploadFile(fileId, { isTrashed: true });
      } catch (error) {
        notifyRepositoryDropzoneError(dispatch, error, 'Failed to delete document.');
      }
    },
    [dispatch, onDocumentsTrashed, patchUploadFile, removeUpload, trashDocuments, uploadFiles]
  );

  const restoreUpload = useCallback(
    async (fileId: string) => {
      const file = findUploadFile(uploadFiles, fileId);
      if (!file?.documentId) return;

      try {
        await restoreDocuments({
          variables: { input: { documentIds: [file.documentId] } },
        });
        onDocumentsRestored?.([file.documentId]);
        patchUploadFile(fileId, { isTrashed: false });
      } catch (error) {
        notifyRepositoryDropzoneError(dispatch, error, 'Failed to restore document.');
      }
    },
    [dispatch, onDocumentsRestored, patchUploadFile, restoreDocuments, uploadFiles]
  );

  const closePanel = useCallback(() => {
    cancelActiveUploads();
    clearInactiveUploads();
    setIsPanelOpen(false);
  }, [cancelActiveUploads, clearInactiveUploads]);

  const handleDropFiles = useCallback(
    (files: File[]) => {
      if (filestackLoading) return;

      setIsPanelOpen(true);

      handleFilesDrop({
        dispatch,
        files,
        onLargeUpload: (largeFiles, totalSize) => {
          setPendingUpload({ files: largeFiles, totalSize });
        },
        onValidFiles: validFiles => {
          void startUpload(validFiles);
        },
      });
    },
    [dispatch, filestackLoading, startUpload]
  );

  const handleDropError = useCallback(
    (error: Error) => {
      notifyRepositoryDropzoneError(dispatch, error, 'Failed to process dropped files.');
    },
    [dispatch]
  );

  const { dragProps, isDragging } = useFileDragState({
    disabled,
    onDropError: handleDropError,
    onDropFiles: handleDropFiles,
  });

  const confirmLargeUpload = useCallback(() => {
    if (!pendingUpload) return;

    void startUpload(pendingUpload.files);
    setPendingUpload(null);
  }, [pendingUpload, startUpload]);

  const cancelLargeUpload = useCallback(() => {
    setPendingUpload(null);
  }, []);

  return {
    cancelActiveUploads,
    cancelLargeUpload,
    closePanel,
    confirmLargeUpload,
    deleteUpload,
    dialog,
    dropzoneProps: dragProps,
    isDragging,
    isPanelOpen,
    isUploadPaused,
    pauseActiveUploads,
    pendingUpload,
    restoreUpload,
    resumeActiveUploads,
    retryUpload,
    uploadFiles,
  };
}

function getIsActiveUpload(file: UploadFile): boolean {
  return file.state === 'pending' || file.state === 'uploading' || file.state === 'persisting';
}

function getIsCancellableUpload(file: UploadFile): boolean {
  return file.state === 'pending' || file.state === 'uploading';
}

function getIsRetryableUpload(file: UploadFile): boolean {
  return file.state === 'failed' || file.state === 'cancelled';
}

function getIsSuccessfulUploadRow(
  row: RepositoryDropzoneUploadStackRow
): row is RepositoryDropzoneSuccessfulStackEntry {
  return row.result !== null;
}
